import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { authApi } from "@/api/api";
import { businessApi } from "@/api/businessApi";
import AddressAutocomplete from "../../../components/AddressAutocomplete";
import {
  validateEmail,
  validatePhone,
  validateRequired,
  validateFields,
  areAllValid,
} from "../../../utils/validation";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";

const PersonalData = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "user@zoho.com"; // Fallback to default email
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  // Business data fields
  const [businessName, setBusinessName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [line1, setLine1] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [phone, setPhone] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [fullAddress, setFullAddress] = useState("");

  // Validation state
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Handle address selection from autocomplete
  const handleAddressSelect = (addressData: {
    line1: string;
    city: string;
    state: string;
    zipCode: string;
  }) => {
    setLine1(addressData.line1);
    setCity(addressData.city);
    setState(addressData.state);
    setZipCode(addressData.zipCode);
    // Note: country field removed from form
  };

  // Validate form fields
  const validateForm = () => {
    const validations = validateFields([
      {
        field: "firstName",
        value: firstName,
        validator: (value) => validateRequired(value, "First name"),
      },
      {
        field: "lastName",
        value: lastName,
        validator: (value) => validateRequired(value, "Last name"),
      },
      {
        field: "businessName",
        value: businessName,
        validator: (value) => validateRequired(value, "Company name"),
      },
      { field: "phone", value: phone, validator: validatePhone },
      {
        field: "businessEmail",
        value: businessEmail,
        validator: validateEmail,
      },
      {
        field: "city",
        value: city,
        validator: (value) => validateRequired(value, "City"),
      },
      {
        field: "state",
        value: state,
        validator: (value) => validateRequired(value, "State"),
      },
      {
        field: "line1",
        value: line1,
        validator: (value) => validateRequired(value, "Address"),
      },
      {
        field: "zipCode",
        value: zipCode,
        validator: (value) => validateRequired(value, "Zip code"),
      },
    ]);

    const errors: Record<string, string> = {};
    Object.entries(validations).forEach(([field, result]) => {
      if (!result.isValid && result.error) {
        errors[field] = result.error;
      }
    });

    setValidationErrors(errors);
    setShowValidationErrors(true);
    return areAllValid(validations);
  };

  // Clear validation error for a specific field
  const clearFieldError = (fieldName: string) => {
    if (validationErrors[fieldName]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleCompletePersonalData = async () => {
    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    // Handle account creation logic here
    console.log("complete personal data:", { firstName, lastName, email });
    try {
      const response = await authApi.completePersonalData(firstName, lastName);
      if (response.status === 200) {
        // Also update business data
        const businessData = {
          business_name: businessName,
          address: {
            city,
            state,
            line_1: line1,
            zip_code: zipCode,
          },
          emails: [businessEmail],
          phones: [
            phone.startsWith("+1")
              ? phone.replace("+", "") // Keep "1" for US numbers: +1xxxxxxxxxx -> 1xxxxxxxxxx
              : phone.replace(/^\+\d{1,3}/, ""),
          ], // Remove country code for other countries
        };

        try {
          await businessApi.updateBusinessData(businessData);
        } catch (businessError) {
          console.error("Error updating business data:", businessError);
        }

        navigate("/post-login");
      }
    } catch (error) {
      console.error("Error completing personal data:", error);
    }
  };

  return (
    <div className="min-h-screen flex justify-center items-center bg-[#F3F5F6] p-4">
      <div className="w-1/2 bg-white rounded-xl p-8">
        {/* Header with Logo and Welcome */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-20 h-20 px-5 pt-[23px] pb-[22px] bg-[#77c6d1] rounded-[909.09px] justify-center items-center inline-flex overflow-hidden">
            <div className="text-white text-[29.09px] font-normal font-['Inter']">
              AC
            </div>
          </div>
          <div>
            <h1 className="text-xl font-semibold mb-1">Welcome!</h1>
            <p className="text-gray-600 text-sm">
              A new Zoho account will be created for the email address
              <br />
              <span className="font-bold text-sm text-black">{email}</span>
            </p>
          </div>
        </div>

        {/* Form Fields */}
        <div className="space-y-6">
          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <input
                type="text"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  clearFieldError("firstName");
                }}
                className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 ${
                  validationErrors.firstName
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-200 focus:ring-green-500"
                }`}
                placeholder="First name"
              />
              {showValidationErrors && validationErrors.firstName && (
                <p className="text-red-500 text-xs mt-1">
                  {validationErrors.firstName}
                </p>
              )}
            </div>
            <div>
              <input
                type="text"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  clearFieldError("lastName");
                }}
                className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 ${
                  validationErrors.lastName
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-200 focus:ring-green-500"
                }`}
                placeholder="Last name"
              />
              {showValidationErrors && validationErrors.lastName && (
                <p className="text-red-500 text-xs mt-1">
                  {validationErrors.lastName}
                </p>
              )}
            </div>
          </div>

          {/* Business Name */}
          <div>
            <input
              type="text"
              value={businessName}
              onChange={(e) => {
                setBusinessName(e.target.value);
                clearFieldError("businessName");
              }}
              className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 ${
                validationErrors.businessName
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-200 focus:ring-green-500"
              }`}
              placeholder="Company Name"
            />
            {showValidationErrors && validationErrors.businessName && (
              <p className="text-red-500 text-xs mt-1">
                {validationErrors.businessName}
              </p>
            )}
          </div>

          {/* Contact Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <PhoneInput
                value={phone}
                onChange={(value) => {
                  setPhone(value);
                  clearFieldError("phone");
                }}
                defaultCountry="us"
                className={`${
                  validationErrors.phone
                    ? "border-red-500 focus-within:ring-red-500"
                    : "border-gray-200 focus-within:ring-green-500"
                }`}
                inputStyle={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  outline: "none",
                }}
                countrySelectorStyleProps={{
                  buttonStyle: {
                    padding: "8px",
                    border: "1px solid #e5e7eb",
                    borderRight: "none",
                    borderRadius: "8px 0 0 8px",
                    backgroundColor: "#f9fafb",
                  },
                }}
              />
              {showValidationErrors && validationErrors.phone && (
                <p className="text-red-500 text-xs mt-1">
                  {validationErrors.phone}
                </p>
              )}
            </div>
            <div>
              <input
                type="email"
                value={businessEmail}
                onChange={(e) => {
                  setBusinessEmail(e.target.value);
                  clearFieldError("businessEmail");
                }}
                className={`w-full p-2 border rounded-lg focus:outline-none focus:ring-2 ${
                  validationErrors.businessEmail
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-200 focus:ring-green-500"
                }`}
                placeholder="Email"
              />
              {showValidationErrors && validationErrors.businessEmail && (
                <p className="text-red-500 text-xs mt-1">
                  {validationErrors.businessEmail}
                </p>
              )}
            </div>
          </div>

          {/* Address Fields - Auto-filled by autocomplete but editable */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="City"
                title="Auto-filled from address selection, but you can edit manually"
              />
            </div>
            <div>
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="w-full p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="State"
                title="Auto-filled from address selection, but you can edit manually"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <input
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                className="w-full p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Zip Code"
                title="Auto-filled from address selection, but you can edit manually"
              />
            </div>
            <div>{/* Empty div to maintain grid layout */}</div>
          </div>

          {/* Address Line with Autocomplete */}
          <div>
            <AddressAutocomplete
              value={fullAddress}
              onChange={setFullAddress}
              onAddressSelect={handleAddressSelect}
              placeholder="Enter your address"
              className="w-full"
            />
          </div>

          {/* Terms Checkbox */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="terms"
              checked={agreeToTerms}
              onChange={(e) => setAgreeToTerms(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-green-500 focus:ring-green-500"
            />
            <label htmlFor="terms" className="text-sm text-gray-600 ">
              I agree to the Terms of service and Privacy policies of Zoho
              Corporation
            </label>
          </div>

          {/* Create Account Button */}
          <div className="flex justify-center">
            <button
              onClick={handleCompletePersonalData}
              disabled={
                !firstName ||
                !lastName ||
                !agreeToTerms ||
                !businessName ||
                !city ||
                !state ||
                !line1 ||
                !zipCode ||
                !phone ||
                !businessEmail
              }
              className="w-[380px] bg-[#0DB17B] text-white py-2 rounded-lg hover:bg-[#0DB17B]/80 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Create account
            </button>
          </div>

          {/* Link Existing Accounts Section */}
          <div className="mt-6 bg-gray-300 p-6 rounded-lg flex justify-between items-center">
            <div className="w-2/3">
              <h3 className="font-medium mb-1">Link existing accounts</h3>
              <p className="text-sm text-gray-600">
                If you have a Zoho account, you can link your Google account
                with it.
              </p>
            </div>
            <button className="bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 font-medium">
              Link Accounts
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalData;
